import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Stack, TextField, Typography} from 'views/components';
import {SearchOutlined as SearchOutlinedIcon} from 'views/icons';
import {translate} from 'views/module';

import backend from 'utils/backend';

const ClientTab = (props) => {
  const {
    onSelectAssignee,
  } = props;

  const [clients, setClients] = React.useState([]);
  const [assigneeSearch, setAssigneeSearch] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  const rootRef = React.useRef(null);

  const assigneeSearchQuery = assigneeSearch.trim().toLowerCase();

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [assigneeSearchQuery]);

  React.useEffect(() => {
    rootRef.current?.focus({preventScroll: true});
  }, [rootRef]);

  React.useEffect(() => {
    let mounted = true;
    const isFirstLoad = assigneeSearchQuery.length === 0;
    setIsLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const filter = [
          {
            key: 'status',
            value: [
              'ACTIVE',
              'LEAD',
              'DETAILS_NEEDED',
              'CG_SEARCH',
              'CG_SEARCH_ACTIVE',
              'CG_PROFILE_SENT',
              'MEET_GREET',
              'STALLED',
            ],
          },
        ];

        if (assigneeSearchQuery) {
          filter.push({key: 'clientName', value: `${assigneeSearchQuery}*`});
        }

        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.client.queryClient(filter, sortBy);

        if (mounted) {
          setClients(result?.list || []);
        }
      } catch (error) {
        console.error('Failed to load clients in assignee picker', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }, isFirstLoad ? 0 : 250);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [assigneeSearchQuery]);

  const handleSearchKeyDown = React.useCallback((event) => {
    const isHandledKey = [
      'ArrowDown',
      'ArrowUp',
      'Enter',
    ].includes(event.key);

    if (!isHandledKey) {
      return;
    }

    event.preventDefault();

    if (!clients.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex + 1) % clients.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex - 1 + clients.length) % clients.length);
      return;
    }

    if (event.key === 'Enter') {
      const highlightedAssignee = clients[highlightedOptionIndex] || clients[0];

      if (highlightedAssignee) {
        onSelectAssignee('clients', highlightedAssignee);
      }
    }
  }, [clients, highlightedOptionIndex, onSelectAssignee]);

  return (
    <Stack
      ref={rootRef}
      spacing={1.5}
      tabIndex={0}
      onKeyDown={handleSearchKeyDown}
    >
      <TextField
        fullWidth
        variant='outlined'
        value={assigneeSearch}
        onChange={setAssigneeSearch}
        placeholder={translate('tasks.Common.AssigneePicker.SearchPlaceholder')}
        icon={SearchOutlinedIcon}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: 290,
          pr: 0.5,
        }}
      >
        <Stack spacing={0.25}>
          {
            isLoading &&
              <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                <CircularProgress size={16} />
              </Box>
          }
          {
            !isLoading && clients.map((client, optionIndex) => (
              <Box
                key={`clients-${client.clientRn}`}
                component='button'
                onMouseEnter={() => setHighlightedOptionIndex(optionIndex)}
                onClick={() => onSelectAssignee('clients', client)}
                sx={{
                  background: 'none',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: optionIndex === highlightedOptionIndex ? '#F7F4EF' : 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  px: 0.75,
                  py: 0.75,
                  color: '#23201C',
                  fontSize: '16px',
                }}
              >
                {client.clientName}
              </Box>
            ))
          }
          {
            !isLoading && clients.length === 0 &&
              <Typography variant='body2' color='text.secondary' sx={{px: 0.75}}>
                {translate('tasks.Common.AssigneePicker.NoClientsFound')}
              </Typography>
          }
        </Stack>
      </Box>
    </Stack>
  );
};

ClientTab.propTypes = {
  onSelectAssignee: PropTypes.func.isRequired,
};

export default React.memo(ClientTab);
